/**
 * Duplicate Campaign — modals A (single) + B (multi) from the
 * 2026-05-06 design handoff (`campaign_modals.html`).
 *
 *  - Single mode (count === 1): editable "New campaign name" with
 *    `<source>_copy` default. Three toggles. Apply queues one
 *    pending_writes row.
 *  - Multi mode (count >= 2): "N selected" banner, no per-name
 *    input — backend auto-suffixes each source name with
 *    `_copy[_n]` on collision. Three toggles apply uniformly.
 *
 * Pipeline (matches the rest of Ads Manager):
 *   POST /api/campaigns/duplicate (1..N source ids in one call)
 *     → backend creates one pending_writes row per source
 *     → drainer cascades GET/POST through ASA per row
 *     → ASA-confirmed toast appears 1-5min later
 * The modal closes on Apply — the user can keep working while
 * duplicates run in the background.
 */

import { useEffect, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import type { ReactNode } from "react";
import { I } from "@/components/Icon";

export type DuplicateCampaignToggles = {
  enable: boolean;
  include_keywords: boolean;
  include_negatives: boolean;
};

export type DuplicateCampaignApplyArgs = {
  /** Sources, in the order the user picked them. */
  campaign_ids: number[];
  /** Single-mode override (optional). For multi-mode this is null. */
  new_name_override?: string | null;
  toggles: DuplicateCampaignToggles;
};

type DuplicateCampaignModalProps = {
  open: boolean;
  onClose: () => void;
  /** When passed, modal is in single mode and pre-fills the name
   *  field with `<name>_copy`. When null, modal is in multi mode. */
  source: { campaign_id: number; name: string } | null;
  /** Required for multi mode. Ignored in single mode (uses source.id). */
  multiCampaignIds?: number[];
  onApply: (args: DuplicateCampaignApplyArgs) => Promise<void>;
};

export function DuplicateCampaignModal({
  open,
  onClose: onCloseProp,
  source,
  multiCampaignIds = [],
  onApply,
}: DuplicateCampaignModalProps) {
  const isSingle = source !== null;
  const [newName, setNewName] = useState("");
  const [enable, setEnable] = useState(false);
  const [includeKeywords, setIncludeKeywords] = useState(true);
  const [includeNegatives, setIncludeNegatives] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewName(source ? `${source.name}_copy` : "");
    setEnable(false);
    setIncludeKeywords(true);
    setIncludeNegatives(true);
    setError(null);
    setBusy(false);
  }, [open, source]);

  const guard = useDiscardGuard(open, (isSingle && source ? newName.trim() !== `${source.name}_copy` : false) || enable || !includeKeywords || !includeNegatives, onCloseProp);
  const onClose = guard.requestClose;

  if (!open) return null;
  if (!isSingle && multiCampaignIds.length === 0) return null;

  const trimmed = newName.trim();
  const canApplySingle = isSingle ? trimmed.length > 0 && trimmed.length <= 240 : true;
  const canApply = !busy && canApplySingle;

  const handleApply = async () => {
    if (!canApply) return;
    setError(null);
    setBusy(true);
    try {
      const ids = isSingle && source
        ? [source.campaign_id]
        : multiCampaignIds;
      await onApply({
        campaign_ids: ids,
        new_name_override: isSingle && trimmed !== `${source!.name}_copy`
          ? trimmed
          : isSingle
          ? trimmed
          : null,
        toggles: {
          enable,
          include_keywords: includeKeywords,
          include_negatives: includeNegatives,
        },
      });
      onCloseProp();
    } catch (err) {
      const detail =
        (err as {
          response?: { data?: { error?: { message?: string } } };
        })?.response?.data?.error?.message ?? "Couldn't queue the duplicate.";
      setError(detail);
      setBusy(false);
    }
  };

  return (
    <div
      className="cm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="cm-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dup-title"
      >
        <div className="cm-head">
          <div>
            <div className="cm-title" id="dup-title">
              Duplicate <em>{isSingle ? "campaign" : "campaigns"}</em>
            </div>
            <div className="cm-sub">
              {isSingle ? (
                <>
                  Create a copy of{" "}
                  <strong>{source!.name}</strong> with the same
                  structure. The new campaign starts paused unless you
                  enable it below.
                </>
              ) : (
                <>
                  Duplicate selected campaigns. Each new campaign keeps
                  its source name with a &ldquo;_copy&rdquo; suffix and
                  the same structure.
                </>
              )}
            </div>
          </div>
          <button className="cm-x" aria-label="Close" onClick={onClose}>
            {I.x}
          </button>
        </div>

        <div className="cm-body">
          {isSingle ? (
            <div className="cm-field">
              <label className="cm-field-label" htmlFor="dup-new-name">
                New campaign name
              </label>
              <input
                id="dup-new-name"
                className="cm-field-input"
                type="text"
                value={newName}
                autoFocus
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleApply();
                }}
                maxLength={300}
              />
              {error ? (
                <div className="cm-field-error">{error}</div>
              ) : (
                <div className="cm-field-hint">
                  Original: {source!.name}
                </div>
              )}
            </div>
          ) : (
            <MultiBanner count={multiCampaignIds.length} />
          )}

          <div className="cm-toggles" role="group" aria-label="Duplicate options">
            <ToggleRow
              label={isSingle ? "Enable new campaign" : "Enable new campaigns"}
              desc={
                isSingle
                  ? "Start the duplicated campaign in the Enabled state. When off, the copy is created paused."
                  : "Start the duplicated campaigns in the Enabled state. When off, copies are created paused."
              }
              checked={enable}
              onChange={setEnable}
            />
            <ToggleRow
              label="Include all keywords"
              desc={
                isSingle
                  ? "Copy every keyword from the source campaign across all ad groups, preserving bids and match types."
                  : "Copy every keyword from each source campaign, preserving bids and match types."
              }
              checked={includeKeywords}
              onChange={setIncludeKeywords}
            />
            <ToggleRow
              label="Include all negative keywords"
              desc={
                isSingle
                  ? "Copy negative keywords at both campaign and ad-group level."
                  : "Copy negative keywords at both campaign and ad-group level for every campaign."
              }
              checked={includeNegatives}
              onChange={setIncludeNegatives}
            />
          </div>

          {!isSingle && error && (
            <div className="cm-field-error">{error}</div>
          )}
        </div>

        <div className="cm-foot">
          <button className="cm-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="cm-btn primary"
            onClick={handleApply}
            disabled={!canApply}
          >
            {busy ? "Queueing…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MultiBanner({ count }: { count: number }) {
  return (
    <div className="cm-multi-banner">
      <div className="cm-multi-banner-ico" aria-hidden>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="8" y="8" width="13" height="13" rx="2" />
          <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
        </svg>
      </div>
      <div className="cm-multi-banner-meta">
        <span className="cm-multi-banner-title">
          Duplicate selected campaigns
        </span>
        <span className="cm-multi-banner-sub">
          All settings, ad groups and creative sets will be copied.
        </span>
      </div>
      <span className="cm-multi-banner-count">{count} selected</span>
    </div>
  );
}

function ToggleRow({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="cm-toggle-row">
      <div className="cm-toggle-meta">
        <span className="cm-toggle-label">{label}</span>
        <span className="cm-toggle-desc">{desc}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={"cm-switch " + (checked ? "on" : "")}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}
